/**
 * src/components/coach/SubscriptionStatusBanner.tsx
 * 
 * Component that displays subscription status banners for coaches.
 * Shows pending approval, suspended account, or other subscription states.
 * Appears at the top of coach dashboard when subscription is not active.
 * Updated to support internationalization with Spanish translations.
 */

import { AlertCircle, Clock, Mail, XCircle } from 'lucide-react';
import { CoachSubscriptionStatus } from '../../hooks/useCoachSubscription';
import { useI18n } from '../../lib/i18n/context';

interface SubscriptionStatusBannerProps {
  status: CoachSubscriptionStatus;
  className?: string;
}

export function SubscriptionStatusBanner({ 
  status, 
  className = '' 
}: SubscriptionStatusBannerProps) {
  const { t } = useI18n();

  // Don't show banner for active or loading states
  if (status === 'active' || status === 'loading' || status === 'none') {
    return null;
  }

  const getBannerConfig = (status: CoachSubscriptionStatus) => {
    switch (status) {
      case 'pending':
        return {
          icon: <Clock className="h-5 w-5" />,
          title: t('subscription-pending-title'),
          message: t('subscription-pending-message'),
          bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
          borderColor: 'border-yellow-200 dark:border-yellow-800',
          textColor: 'text-yellow-800 dark:text-yellow-200',
          iconColor: 'text-yellow-600 dark:text-yellow-400',
        };
      case 'suspended':
        return {
          icon: <XCircle className="h-5 w-5" />,
          title: t('subscription-suspended-title'),
          message: t('subscription-suspended-message'),
          bgColor: 'bg-red-50 dark:bg-red-900/20',
          borderColor: 'border-red-200 dark:border-red-800',
          textColor: 'text-red-800 dark:text-red-200',
          iconColor: 'text-red-600 dark:text-red-400',
        };
      case 'cancelled':
        return {
          icon: <AlertCircle className="h-5 w-5" />,
          title: t('subscription-cancelled-title'),
          message: t('subscription-cancelled-message'),
          bgColor: 'bg-gray-50 dark:bg-gray-900/20',
          borderColor: 'border-gray-200 dark:border-gray-800',
          textColor: 'text-gray-800 dark:text-gray-200',
          iconColor: 'text-gray-600 dark:text-gray-400',
        };
      default:
        return {
          icon: <AlertCircle className="h-5 w-5" />,
          title: t('subscription-unknown-title'),
          message: t('subscription-unknown-message'),
          bgColor: 'bg-gray-50 dark:bg-gray-900/20',
          borderColor: 'border-gray-200 dark:border-gray-800',
          textColor: 'text-gray-800 dark:text-gray-200',
          iconColor: 'text-gray-600 dark:text-gray-400',
        };
    }
  };

  const config = getBannerConfig(status);

  return (
    <div className={`rounded-lg border p-4 ${config.bgColor} ${config.borderColor} ${className}`}>
      <div className="flex items-start">
        <div className={`flex-shrink-0 ${config.iconColor}`}>
          {config.icon}
        </div>
        <div className="ml-3 flex-1">
          <div className={`text-sm font-medium ${config.textColor}`}>
            {config.title}
          </div>
          <div className={`mt-1 text-sm ${config.textColor} opacity-90`}>
            {config.message}
          </div>
          
          {/* Contact support section for non-pending statuses */}
          {status !== 'pending' && (
            <div className="mt-3">
              <button className={`inline-flex items-center text-sm font-medium ${config.textColor} hover:underline focus:outline-none focus:underline`}>
                <Mail className="h-4 w-4 mr-1" />
                {t('contact-support')}
              </button>
            </div>
          )}
          
          {/* Additional info for pending status */}
          {status === 'pending' && (
            <div className={`mt-2 text-xs ${config.textColor} opacity-75`}>
              <p>{t('subscription-pending-info')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 